import {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import {matchPath} from 'react-router-dom';

import routers, {buildPath, ServerConfig} from '@/config/routers';
import * as store from '@/store';
import sdk from '@/sdk';
import {addParamToCustomUrl, getParamFromCustomUrl} from '@/utils/kuQuery';
import {blockIdKey} from '@/constants/common/searchParamKeys';
import Dialog from '@/components/Dialog2';
import {shareToRuLiu} from '@/components/Share/blockRefShare';

import styles from './index.module.scss';

function openDialogOfDelRef(onConfirm: () => void) {
    let rootEl: HTMLDivElement | null = document.createElement('div');
    document.body.appendChild(rootEl);

    function remove() {
        if (rootEl) {
            ReactDOM.unmountComponentAtNode(rootEl);
            document.body.removeChild(rootEl);
            rootEl = null;
        }
    }

    function confirm() {
        onConfirm();
        remove();
    }

    ReactDOM.render(
        <Dialog visible onCancel={remove} onConfirm={confirm}>
            <div className={styles.title}>解除引用</div>
            <div className={styles.content}>
                解除后，其他引用场景无法看到该内容，且引用关系不能恢复。
            </div>
        </Dialog>,
        rootEl
    );
}

export const useTransclusionConfig = (curDocId: string) => {
    return {
        // 开启块引用功能，默认关闭
        enable: true,
        // 返回包含文档id、blockId等信息的url
        getRefLinkByBlockId(
            blockId: string,
            sourceUrl?: string,
            type?: string
        ) {
            const curDoc = store.getCurDocument();
            if (!curDoc || !blockId) {
                return;
            }

            if (sourceUrl) {
                let urlObj = addParamToCustomUrl(
                    sourceUrl,
                    blockIdKey,
                    blockId
                );
                if (type === 'card') {
                    urlObj = addParamToCustomUrl(urlObj.href, 'type', type);
                }
                return urlObj.href;
            } else {
                const path = buildPath(routers.Knowledge.docPath, {
                    spaceGuid: curDoc.spaceGuid!,
                    groupGuid: curDoc.groupGuid!,
                    repositoryGuid: curDoc.repositoryGuid,
                    docGuid: curDoc.docGuid,
                });

                return `${ServerConfig.origin}${path}?${blockIdKey}=${blockId}${
                    type === 'card' ? `&type=${type}` : ''
                }`;
            }
        },
        // 解析url返回blockId、docId
        getRefInfoByUrl(url: string) {
            const urlObj = new URL(url);
            const match = matchPath<{docGuid: string}>(urlObj.pathname, {
                path: routers.Knowledge.docPath,
            });
            const docId = match ? match.params.docGuid : '';
            const blockId = getParamFromCustomUrl(url, blockIdKey);

            return blockId && docId
                ? {sourceBlockId: blockId, sourceDocId: docId}
                : null;
        },
        getBlockIdByRefLink(url: string) {
            const blockId = getParamFromCustomUrl(url, blockIdKey);
            return blockId;
        },
        // 是否是引用块的URL
        isRefBlockLink(url: string) {
            try {
                const urlObj = new URL(url);
                const match = matchPath(urlObj.pathname, {
                    path: routers.Knowledge.docPath,
                });
                const blockId = getParamFromCustomUrl(url, blockIdKey);
                return Boolean(match && blockId);
            } catch (err) {
                return false;
            }
        },
        // 申请页面权限
        applyRefPermission(docUrl: string) {
            window.open(docUrl);
        },
        deleteRefBlock(onConfirm: () => void) {
            openDialogOfDelRef(onConfirm);
        },
        async getRelatedBlocksData(blockId: string, docId?: string) {
            const sourceDocId = docId || curDocId;
            const ks = await sdk.getKsServer();
            const result = await ks.getBlockRefList({
                blockId,
                docGuid: sourceDocId,
            });

            const list = (result.reverseCiteContent || []).map((item: any) => {
                let url = item.url;
                const isSource = item.reverseCiteDoc === sourceDocId;
                const block = item.blocks ? item.blocks[0] : null;
                const blockIdStr = isSource ? blockId : block?.blockId;
                if (blockIdStr) {
                    const urlObj = addParamToCustomUrl(
                        url,
                        blockIdKey,
                        blockIdStr
                    );
                    url = urlObj.href;
                }

                return {
                    title: item.title,
                    url,
                    isSource,
                    isPublic: item.isPublic,
                };
            });

            list.sort((item: any) => (item.isSource ? -1 : 0));

            if (result.reverseCiteRlChats && result.reverseCiteRlChats.length) {
                list.unshift({iMrefed: true});
            }

            if (list.length === 0) {
                return Promise.reject(list);
            }

            return list;
        },
        shareToIm: !(window as any).isSdkPage ? (url: string, type: string) => {
            let shareUrl = url;
            if (type === 'card') {
                // 卡片分享没有type参数的时候，手动补上
                const hasType = getParamFromCustomUrl(url, 'type');
                if (!hasType) {
                    shareUrl = addParamToCustomUrl(url, 'type', type).href;
                }
            }
            shareToRuLiu(shareUrl);
        } : undefined,
    };
};
